/* eslint-disable react-hooks/exhaustive-deps */
import { NextPageWithLayout } from "next";
import { useRouter } from "next/router";
import { ChangeEvent, ReactElement, useEffect, useState } from "react";

import { IndexService, PageStatus, Pagination, ServiceType } from "@/apis/clients";
import { ApiResponseError, ApiValidationError } from "@/apis/errors";
import serviceApi from "@/apis/service/serviceApi";
import { ButtonWithIcon } from "@/components/atoms/buttons/ButtonWithIcon";
import { Select } from "@/components/atoms/inputForms/Select";
import PaginationComponent from "@/components/molecules/Pagination";
import { PageHeader } from "@/components/organisms/header/PageHeader";
import { NoPostMessage } from "@/components/organisms/not-found/NoPostMessage";
import { SpKeywordSearch } from "@/components/organisms/search/SpKeywordSearch";
import { ServiceList } from "@/components/organisms/services/ServiceList";
import { ServiceSearchModal } from "@/components/organisms/services/ServiceSearchModal";
import { ServicesSearchField } from "@/components/organisms/services/ServicesSearchField";
import { ServiceDeleteModal } from "@/components/organisms/services/modals/ServiceDeleteModal";
import ServiceUnDeletableModal from "@/components/organisms/services/modals/ServiceUnDeletableModal";
import { DEFAULT_TAB } from "@/components/organisms/services/show/ServiceDetailTabs";
import { LayoutTemplate } from "@/components/templates/layout";
import { useSystemMessage } from "@/hooks/systemMessage";
import useApiResponseErrorHandler from "@/hooks/useApiResponseErrorHandler";
import {
  ServiceSearchConditions,
  ServiceSortBy,
  ServiceSortOptions,
  isServiceSortBy,
  isServiceType,
} from "@/models/service/service";
import { useLoading } from "@/states/loading/operations";
import { NEXT_URL } from "@/utils/const";

import PlusIcon from "/public/icon/plus.svg";
import ErrorLimitByPlanModal from "@/components/organisms/modals/ErrorLimitByPlanModal";
import { UpgradeAlertReason } from "@/utils/upgrade-alert";
import { accountApi } from "@/apis/accounts/accountApi";
import { pageApi } from "@/apis/pages/pageApi";
import { useGetAccount } from "@/states/auth/operations";

export type ServiceSearchRequest = {
  sortBy: "UPDATED" | "SALES";
  keyword?: string;
  updatedAtFrom?: string;
  updatedAtTo?: string;
  type?: Array<ServiceType>;
  page?: number;
};

const title = "コンテンツ一覧";

interface QueryParams {
  // group: string;
  sortBy?: string;
  keyword?: string;
  type?: string;
  updatedAtFrom?: string;
  updatedAtTo?: string;
  page?: string;
  per?: string;
}

interface State {
  conditions: ServiceSearchConditions;
  pagination: Pagination | undefined;
  isValidConditions: boolean;
  currentPage: number;
  isOpenSearchModal: boolean;
  isOpenDeletableModal: boolean;
  isOpenUnDeletableModal: boolean;
  services: IndexService[];
  selectedServiceForDelete: IndexService | undefined;
}

const initialSearchConditions: ServiceSearchConditions = {
  // types: [],
  // group: "all",
  sortBy: "UPDATED",
  keyword: "",
  type: "",
  updatedAtFrom: "",
  updatedAtTo: "",
};

const DEFAULT_PER_PAGE = 50;

const ServiceIndex: NextPageWithLayout = () => {
  const router = useRouter();
  const systemMessage = useSystemMessage();
  const withLoading = useLoading();
  const { handlePageError } = useApiResponseErrorHandler();
  const [state, setState] = useState<State>({
    conditions: initialSearchConditions,
    pagination: undefined,
    isValidConditions: true,
    currentPage: 1,
    isOpenSearchModal: false,
    isOpenDeletableModal: false,
    isOpenUnDeletableModal: false,
    services: [],
    selectedServiceForDelete: undefined,
  });

  const isOpenDeleteModal = !!state.selectedServiceForDelete;
  const getAccount = useGetAccount();

  const hasCreateNewPage = async () => {
    const response = await accountApi.getAccount();

    return Number(response.data.total_public_content_allowed) >= Number(response.data.user_plan_config?.max_content_public);
  };

  const handleClickDelete = async (service: IndexService) => {
    try {
      await withLoading(async () => {
        const { data } = await serviceApi.isDeletable({ id: service.id });
        if (data.is_deletable) {
          openDeleteModal(service);
          return;
        }
        openUnDeletableModal();
      });
    } catch (e) {
      if (e instanceof ApiResponseError) {
        await handlePageError(e);
      }
    }
  };

  const openDeleteModal = (service: IndexService) => {
    setState((prev) => ({
      ...prev,
      selectedServiceForDelete: service,
    }));
  };
  const openUnDeletableModal = () => {
    setState((prev) => ({
      ...prev,
      isOpenUnDeletableModal: true,
    }));
  };

  const closeModal = () => {
    setState((prev) => ({
      ...prev,
      isOpenUnDeletableModal: false,
      selectedServiceForDelete: undefined,
    }));
  };

  const handleOpenSearchModal = () => {
    // setIsOpenSearchModal(true);
    setState((prev) => ({
      ...prev,
      isOpenSearchModal: true,
    }));
  };

  const handleCloseSearchModal = () => {
    setState((prev) => ({
      ...prev,
      isOpenSearchModal: false,
    }));
  };

  const handleOnChange = (
    ev: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setState((prev) => ({
      ...prev,
      conditions: {
        ...prev.conditions,
        keyword: ev.target.value,
      },
    }));
  };

  const handleDeleteService = async (service: IndexService) => {
    try {
      await withLoading(async () => {
        await serviceApi.deleteService({ id: service.id });
        await submitSearch(state.conditions, state.currentPage);
      });
      closeModal();
      systemMessage.success("削除しました。");
    } catch (e) {
      if (e instanceof ApiResponseError) {
        await handlePageError(e);
      }
    }
  };

  const handleDuplicateService = async (id: number, status: number) => {
    try {
      await withLoading(async () => {
        const res = await serviceApi.duplicateService({ id });
        const _id = res.data.id;
        if (_id)
          await router.push({
            pathname: NEXT_URL.SERVICES_DETAIL,
            query: { id: _id, tab: DEFAULT_TAB },
          });
      });
      systemMessage.success("複製しました。");
    } catch (e) {
      if (e instanceof ApiValidationError) {
        if (
          e?.errors?.["service.service_menus"] ||
          e?.errors?.subscription_menu_limit ||
          JSON.stringify(e.errors ?? {}).includes("メニュー数上限")
        ) {
          setUpgradeReason("SUBSCRIPTION_MENU_LIMIT");
          setIsPopupVisible(true);
          return;
        }
        if (e?.errors?.total_page_limit) {
          setUpgradeReason("PAGE_PUBLISH_LIMIT");
          setIsPopupVisible(true);
          return;
        }
        const errorMessageArr = Object.values(e.errors).map((value) => {
          return value.join("\n");
        });
        const errorMessages = errorMessageArr.join("\n");
        systemMessage.danger(errorMessages);
        return;
      }

      if (e instanceof ApiResponseError) {
        await handlePageError(e);
      }
    }
  };

  const handlePublishPage = async (serviceId:number, id?: number, bool?: boolean) => {
    await withLoading(async () => {
      if (!id) return;
      try {
        const res = await pageApi.putPageStatus({
          id,
          updatePageStatus: {
            status: bool ? PageStatus.PRIVATE : PageStatus.PUBLIC,
          },
        });
        await getAccount();
        setState((prev) => ({
          ...prev,
          services: prev.services.map((item) => ({
            ...item,
            status: item.id === serviceId ? res.data.status : item.status,
            page: {
              ...item.page,
              status: item.id === serviceId ? res.data.status : item.page?.status
            }
          })),
        }));
      } catch (e) {
        if (e instanceof ApiValidationError) {
          if (
            e?.errors?.["service.service_menus"] ||
            e?.errors?.subscription_menu_limit ||
            JSON.stringify(e.errors ?? {}).includes("メニュー数上限")
          ) {
            setUpgradeReason("SUBSCRIPTION_MENU_LIMIT");
            setIsPopupVisible(true);
            return;
          }
          if (e?.errors?.total_page_limit) {
            setUpgradeReason("PAGE_PUBLISH_LIMIT");
            setIsPopupVisible(true);
            return;
          }
        }
        if (e instanceof ApiResponseError) {
          await handlePageError(e);
        }
      }
    });
  };

  const fetchServicesByConditions = async (
    searchConditions: ServiceSearchConditions,
    pageSetting: { page: number; perPage: number },
  ) => {
    validateSearchConditions();
    const request: ServiceSearchRequest = {
      sortBy: searchConditions.sortBy,
      type: searchConditions.type ? [searchConditions.type] : undefined,
      updatedAtFrom: searchConditions.updatedAtFrom
        ? searchConditions.updatedAtFrom
        : undefined,
      updatedAtTo: searchConditions.updatedAtTo
        ? searchConditions.updatedAtTo
        : undefined,
      keyword: searchConditions.keyword ? searchConditions.keyword : undefined,
      ...pageSetting,
    };
    return await withLoading(async () => {
      return await serviceApi.indexService(request);
    });
  };

  const submitSearch = async (
    searchConditions: ServiceSearchConditions,
    page: number,
  ) => {
    await router.push({
      pathname: NEXT_URL.SERVICES,
      query: {
        ...router.query,
        ...searchConditions,
        page: page,
      },
    });
  };

  // 検索条件をクリアする
  const clearConditions = () => {
    setState((prev) => ({
      ...prev,
      conditions: {
        // group: state.conditions.group,
        sortBy: state.conditions.sortBy,
        keyword: initialSearchConditions.keyword,
        type: initialSearchConditions.type,
        updatedAtFrom: initialSearchConditions.updatedAtFrom,
        updatedAtTo: initialSearchConditions.updatedAtTo,
      },
    }));
  };

  // 検索条件が設定されているかチェックする.
  // 設定されていた場合、検索結果0件だと指定の条件ではない表示が必要なため
  const validateSearchConditions = () => {
    const c = state.conditions;
    // ステータスが全て以外は条件あり
    if (c.type !== "") {
      setState((prev) => ({
        ...prev,
        isValidConditions: true,
      }));
      return;
    }
    // キーワード、更新日に条件あり
    if (c.keyword || c.updatedAtFrom || c.updatedAtTo) {
      setState((prev) => ({
        ...prev,
        isValidConditions: true,
      }));
      return;
    }

    setState((prev) => ({
      ...prev,
      isValidConditions: false,
    }));
  };

  const fetchAllServices = async () => {
    await submitSearch({
      ...initialSearchConditions,
      sortBy: state.conditions.sortBy,
    }, 1);

    setState((prev) => ({
      ...prev,
      isOpenSearchModal: false,
      conditions: {
        ...initialSearchConditions,
        sortBy: state.conditions.sortBy,
      }
    }));
  };

  useEffect(() => {
    const initialFetch = async (
      conditions: ServiceSearchConditions,
      pageSetting: { page: number; perPage: number },
    ) => {
      try {
        const { data, pagination } = await fetchServicesByConditions(
          conditions,
          pageSetting,
        );
        setState((prev) => ({
          ...prev,
          services: data,
          pagination: pagination,
        }));
      } catch (e) {
        if (e instanceof ApiResponseError) {
          await handlePageError(e);
          return;
        }
        throw e;
      }
    };

    if (router.isReady) {
      const params = router.query as unknown as QueryParams;
      const currentPage = parseInt(params.page ?? "1");
      const perPage = params.per ? +params.per : DEFAULT_PER_PAGE;
      const type = (): ServiceType | "" => {
        if (typeof params.type === "undefined") {
          return "";
        }

        const type = parseInt(params.type);

        if (Number.isNaN(type)) {
          return "";
        }

        if (isServiceType(type)) {
          return type;
        }

        return "";
      };
      const sortBy = (): ServiceSortBy => {
        if (typeof params.sortBy === "undefined") {
          return "UPDATED";
        }
        if (isServiceSortBy(params.sortBy)) {
          return params.sortBy;
        }
        return "UPDATED";
      };
      const conditions: ServiceSearchConditions = {
        sortBy: sortBy(),
        keyword: params.keyword ?? "",
        type: type(),
        updatedAtFrom: params.updatedAtFrom ?? "",
        updatedAtTo: params.updatedAtTo ?? "",
      };
      setState((prev) => ({
        ...prev,
        conditions: conditions,
      }));
      void initialFetch(conditions, { page: currentPage, perPage });
    }
  }, [router.query, router]);

  const selectedSortBy = () => {
    const idx = ServiceSortOptions.findIndex(
      (o) => o.value === state.conditions.sortBy,
    );
    if (idx < 0) {
      return ServiceSortOptions[0];
    }
    return ServiceSortOptions[idx];
  };

  const submitSort = (sortBy: ServiceSortBy) => {
    setState((prev) => {
      const newState = {
        ...prev,
        conditions: {
          ...prev.conditions,
          sortBy: sortBy,
        },
      };
      void submitSearch(newState.conditions, 1);
      return newState;
    });
  };

  function getPagination() {
    return (
      state.pagination && (
        <div className="flex flex-col gap-y-2 items-center space-x-10 md:flex-row md:ml-auto">
          <PaginationComponent
            current={state.pagination.current ?? 1}
            max={state.pagination.last ?? 1}
            pushPage={async (page, per) => {
              await router.push({
                pathname: NEXT_URL.SERVICES,
                query: {
                  ...state.conditions,
                  page: page,
                  per,
                },
              });
            }}
            perPage={state.pagination.per}
            total={state.pagination.total}
          />
        </div>
      )
    );
  }

  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [upgradeReason, setUpgradeReason] =
    useState<UpgradeAlertReason>("PAGE_PUBLISH_LIMIT");

  const handleClosePopup = () => {
    setIsPopupVisible(false);
  };

  return (
    <>
      {isPopupVisible && <ErrorLimitByPlanModal onClose={handleClosePopup} reason={upgradeReason} />}
      {isOpenDeleteModal && state.selectedServiceForDelete && (
        <ServiceDeleteModal
          serviceName={state.selectedServiceForDelete.name}
          isOpen={isOpenDeleteModal}
          close={closeModal}
          submit={() =>
            void handleDeleteService(
              state.selectedServiceForDelete as IndexService,
            )
          }
        />
      )}
      {state.isOpenUnDeletableModal && (
        <ServiceUnDeletableModal
          isOpen={state.isOpenUnDeletableModal}
          close={closeModal}
        />
      )}
      <ServiceSearchModal
        isOpen={state.isOpenSearchModal}
        close={handleCloseSearchModal}
        newParams={state.conditions}
        setNewParams={(params) => {
          setState((prev) => ({
            ...prev,
            conditions: params,
          }));
        }}
        handleOnChange={handleOnChange}
        handleClear={clearConditions}
        handleSearch={() => submitSearch(state.conditions, 1)}
        serviceList={state.services}
        handleDuplicateService={(id, status) => void handleDuplicateService(id, status)}
        handlePublishPage={(serviceId, id, status) => void handlePublishPage(serviceId, id, status)}
        handleClickDelete={handleClickDelete}
      />
      <article>
        <div className="justify-between items-center">
          <PageHeader title={title} />
          <div className="md:my-6 my-4 flex justify-end">
            <ButtonWithIcon
              icon={<PlusIcon className="w-4 h-4 fill-white ml-1 mr-2" />}
              label="新規コンテンツ作成"
              size="md"
              spSize="md-fix"
              className="md:inline-flex md:w-auto text-base font-bold"
              onClick={() => { void router.push(NEXT_URL.CONTENT_CREATE); }}
            />
          </div>
        </div>
        <nav className="py-4 md:py-6 md:pl-8 md:pr-6 bg-theme-thin rounded-lg">
          <div className="hidden md:block">
            <ServicesSearchField
              handleOnChange={handleOnChange}
              handleClear={clearConditions}
              handleSearch={() => submitSearch(state.conditions, 1)}
              serviceSearchConditions={state.conditions}
              setServiceSearchConditions={(c) => {
                setState((prev) => ({
                  ...prev,
                  conditions: c,
                }));
              }}
            />
          </div>
          <SpKeywordSearch
            value={state.conditions.keyword}
            handleOnChangeInput={handleOnChange}
            handleOpenSearchModal={handleOpenSearchModal}
            handleSubmit={() => submitSearch(state.conditions, 1)}
            handleAllServicesShow={fetchAllServices}
          />
        </nav>
        <div className="mt-4 md:mt-8">
          {state.services.length !== 0 ? (
            <>
              <div className="justify-between items-center ml-auto md:flex">
                <Select
                  onChange={(selected) => {
                    void submitSort(selected as ServiceSortBy);
                  }}
                  options={ServiceSortOptions}
                  value={selectedSortBy().value}
                  className="md:w-[200px]"
                  size="sm"
                />
                <div className="hidden md:block">{getPagination()}</div>
              </div>

              <ServiceList
                services={state.services}
                handleDuplicateService={(id, status) => void handleDuplicateService(id, status)}
                handlePublishPage={(serviceId, id, status) => void handlePublishPage(serviceId, id, status)}
                handleClickDelete={handleClickDelete}
              />
              <div className="flex justify-center mt-4 md:justify-end md:ml-auto">
                {getPagination()}
              </div>
            </>
          ) : (
            <>
              {!state.isValidConditions ? (
                <NoPostMessage
                  text="コンテンツを登録してみましょう"
                  button="新規コンテンツ作成"
                  buttonHandle={() => { void router.push(NEXT_URL.CONTENT_CREATE); }}
                />
              ) : (
                <NoPostMessage text="指定の条件は見つかりませんでした" />
              )}
            </>
          )}
        </div>
      </article>
    </>
  );
};

ServiceIndex.getLayout = function getLayout(children: ReactElement) {
  return <LayoutTemplate title={title}>{children}</LayoutTemplate>;
};

export default ServiceIndex;
